(() => {
  if (window.IdreamServicePriceList) {
    window.IdreamServicePriceList.initWithin();
    return;
  }

  const roots = new WeakMap();
  const responseCache = new Map();
  const pendingRequests = new Map();
  const mobileQuery = window.matchMedia('(max-width: 749px)');

  class ServicePriceList {
    constructor(root) {
      this.root = root;
      this.catalog = root.querySelector('[data-price-list-catalog]');
      this.tabs = root.querySelector('[data-price-list-tabs]');
      this.panel = root.querySelector('[data-price-list-panel]');
      this.hero = root.querySelector('[data-price-list-hero]');
      this.config = this.readConfig();
      this.devices = [];
      this.deviceDetails = new Map();
      this.selectedChoiceByDevice = new Map();
      this.renderRevision = 0;
      this.onClick = this.onClick.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onPrefetchIntent = this.onPrefetchIntent.bind(this);
      this.onMediaChange = this.onMediaChange.bind(this);
    }

    async init() {
      if (!this.catalog || !this.tabs || !this.panel || !this.config) return;

      this.root.dataset.initialized = 'true';
      this.root.addEventListener('click', this.onClick);
      this.root.addEventListener('keydown', this.onKeyDown);
      this.tabs.addEventListener('pointerover', this.onPrefetchIntent);
      this.tabs.addEventListener('focusin', this.onPrefetchIntent);
      mobileQuery.addEventListener('change', this.onMediaChange);

      try {
        const response = await this.requestPrices();
        this.devices = orderDevices(asArray(response.devices), this.config.presentation.order);
        this.renderHero();
        this.renderTabs();

        if (this.devices.length === 0) {
          this.renderEmpty();
          return;
        }

        await this.selectDevice(this.devices[0].id);
      } catch {
        this.renderError();
      }
    }

    destroy() {
      this.renderRevision += 1;
      this.root.removeEventListener('click', this.onClick);
      this.root.removeEventListener('keydown', this.onKeyDown);
      this.tabs?.removeEventListener('pointerover', this.onPrefetchIntent);
      this.tabs?.removeEventListener('focusin', this.onPrefetchIntent);
      mobileQuery.removeEventListener('change', this.onMediaChange);
      roots.delete(this.root);
    }

    readConfig() {
      const configId = this.root.dataset.configId;
      const configNode = configId ? document.getElementById(configId) : null;
      if (!configNode) return null;

      try {
        const config = JSON.parse(configNode.textContent || '{}');
        const endpoint = String(config.endpoint || '');
        if (!endpoint.startsWith('/') || endpoint.startsWith('//')) return null;

        const labelDefaults = {
          models: 'Modele urządzeń',
          services: 'Usługi serwisowe',
          serviceHeading: 'Usługa',
          modelHeading: 'Urządzenie',
          priceHeading: 'Koszt',
          loading: 'Ładowanie cennika...',
          empty: 'Brak dostępnych cen dla wybranego urządzenia.',
          error: 'Nie udało się pobrać cennika. Spróbuj ponownie.',
          retry: 'Spróbuj ponownie',
        };
        const configuredLabels = config.labels && typeof config.labels === 'object' ? config.labels : {};

        return {
          ...config,
          endpoint,
          layout: config.layout === 'alternative' ? 'alternative' : 'default',
          presentation: normalizePresentation(config.presentation),
          labels: Object.fromEntries(
            Object.entries(labelDefaults).map(([key, fallback]) => {
              const value = configuredLabels[key];
              return [key, typeof value === 'string' && value.trim() ? value : fallback];
            }),
          ),
        };
      } catch {
        return null;
      }
    }

    buildUrl(deviceId = '') {
      const url = new URL(this.config.endpoint, window.location.origin);
      url.searchParams.set('config', this.config.portalConfig || 'default');
      if (deviceId) url.searchParams.set('device', deviceId);
      return `${url.pathname}${url.search}`;
    }

    requestPrices(deviceId = '', { refresh = false } = {}) {
      const url = this.buildUrl(deviceId);
      if (refresh) responseCache.delete(url);
      if (responseCache.has(url)) return Promise.resolve(responseCache.get(url));
      if (pendingRequests.has(url)) return pendingRequests.get(url);

      const request = fetch(url, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      })
        .then(async (response) => {
          const payload = await response.json().catch(() => ({}));
          if (!response.ok) throw new Error(payload.error || this.config.labels.error);
          responseCache.set(url, payload);
          return payload;
        })
        .finally(() => pendingRequests.delete(url));

      pendingRequests.set(url, request);
      return request;
    }

    prefetchDevice(deviceId) {
      if (!deviceId) return;
      void this.requestPrices(deviceId).catch(() => {});
    }

    renderTabs() {
      this.tabs.replaceChildren();
      this.tabs.style.setProperty('--device-count', String(this.devices.length));

      this.devices.forEach((device, index) => {
        const presentation = this.config.presentation.devices[device.id] || {};
        const title = presentation.label || device.title || device.id;
        const imageSource = presentation.icon || device.image;
        const tab = document.createElement('button');
        tab.id = `${this.root.id}-tab-${index + 1}`;
        tab.className = 'idream-service-price-list__tab';
        tab.type = 'button';
        tab.dataset.deviceId = device.id;
        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-controls', this.panel.id);
        tab.setAttribute('aria-selected', 'false');
        tab.tabIndex = -1;

        if (imageSource) {
          const image = document.createElement('img');
          image.className = 'idream-service-price-list__tab-image';
          image.src = imageSource;
          image.alt = '';
          image.loading = 'eager';
          image.decoding = 'async';
          if (presentation.width) {
            image.classList.add('idream-service-price-list__tab-image--presentation');
            image.style.setProperty('--idream-price-tab-icon-width', `${presentation.width}px`);
          }
          image.dataset.fallbackSource = presentation.icon && device.image ? device.image : '';
          image.addEventListener('error', () => {
            if (image.dataset.fallbackSource) {
              image.src = image.dataset.fallbackSource;
              image.dataset.fallbackSource = '';
              return;
            }
            image.replaceWith(this.createImageFallback(title));
          });
          tab.append(image);
        } else {
          tab.append(this.createImageFallback(title));
        }

        tab.append(createTextElement('span', title));
        this.tabs.append(tab);
      });
    }

    createImageFallback(title) {
      const fallback = document.createElement('span');
      fallback.className = 'idream-service-price-list__tab-fallback';
      fallback.setAttribute('aria-hidden', 'true');
      fallback.textContent = String(title).slice(0, 1).toUpperCase();
      return fallback;
    }

    renderHero() {
      if (!this.hero) return;
      this.hero.replaceChildren();

      if (this.config.presentation.hero) {
        const image = document.createElement('img');
        image.className = 'idream-service-price-list__hero-artwork';
        image.src = this.config.presentation.hero;
        image.alt = '';
        image.loading = 'eager';
        image.decoding = 'async';
        image.fetchPriority = 'high';
        image.addEventListener('error', () => this.renderDynamicHero(), { once: true });
        this.hero.append(image);
        this.hero.hidden = false;
        return;
      }

      this.renderDynamicHero();
    }

    renderDynamicHero() {
      if (!this.hero) return;
      this.hero.replaceChildren();

      const devicesWithImages = this.devices.filter((device) => device.image).slice(0, 6);
      if (devicesWithImages.length === 0) {
        this.hero.hidden = true;
        return;
      }

      devicesWithImages.forEach((device, index) => {
        const image = document.createElement('img');
        image.className = 'idream-service-price-list__hero-image';
        image.src = device.image;
        image.alt = '';
        image.loading = 'eager';
        image.decoding = 'async';
        image.addEventListener('error', () => image.remove(), { once: true });
        if (index === 2) image.fetchPriority = 'high';
        this.hero.append(image);
      });
      this.hero.hidden = false;
    }

    async selectDevice(deviceId, { focus = false, refresh = false } = {}) {
      const device = this.devices.find((item) => item.id === deviceId);
      if (!device) return;

      const revision = ++this.renderRevision;
      this.updateActiveTab(deviceId, focus);

      const cachedDevice = !refresh && this.deviceDetails.get(deviceId);
      if (cachedDevice) {
        this.renderDevice(cachedDevice);
        return;
      }

      this.renderLoading();
      try {
        const response = await this.requestPrices(deviceId, { refresh });
        if (revision !== this.renderRevision) return;
        const detailedDevice = asArray(response.devices).find((item) => item.id === deviceId);
        if (!detailedDevice) {
          this.renderEmpty();
          return;
        }
        this.deviceDetails.set(deviceId, detailedDevice);
        this.renderDevice(detailedDevice);
      } catch {
        if (revision === this.renderRevision) this.renderError(deviceId);
      }
    }

    updateActiveTab(deviceId, focus) {
      const tabs = [...this.tabs.querySelectorAll('[data-device-id]')];
      tabs.forEach((tab) => {
        const isActive = tab.dataset.deviceId === deviceId;
        tab.setAttribute('aria-selected', String(isActive));
        tab.tabIndex = isActive ? 0 : -1;
        if (!isActive) return;
        this.panel.setAttribute('aria-labelledby', tab.id);
        if (focus) tab.focus();
      });
    }

    renderDevice(device) {
      const models = flattenModels(device);
      const choices = this.config.layout === 'alternative' ? groupServices(models) : models;
      if (choices.length === 0) {
        this.renderEmpty();
        return;
      }

      const selectedId = this.selectedChoiceByDevice.get(device.id);
      const activeChoice = choices.find((choice) => choice.id === selectedId) || choices[0];
      this.selectedChoiceByDevice.set(device.id, activeChoice.id);

      const body = document.createElement('div');
      body.className = 'idream-service-price-list__body';
      body.dataset.deviceId = device.id;

      const choiceList = document.createElement('div');
      choiceList.className = 'idream-service-price-list__choices';
      choiceList.dataset.priceListChoices = '';
      choiceList.setAttribute(
        'aria-label',
        this.config.layout === 'alternative' ? this.config.labels.services : this.config.labels.models,
      );

      choices.forEach((choice) => {
        const button = document.createElement('button');
        button.className = 'idream-service-price-list__choice';
        button.type = 'button';
        button.dataset.choiceId = choice.id;
        button.setAttribute('aria-expanded', String(choice.id === activeChoice.id));
        button.setAttribute('aria-controls', `${this.root.id}-details`);
        button.textContent = choice.title;
        choiceList.append(button);
      });

      const details = document.createElement('div');
      details.id = `${this.root.id}-details`;
      details.className = 'idream-service-price-list__details';
      details.dataset.priceListDetails = '';

      body.append(choiceList, details);
      this.panel.replaceChildren(body);
      this.catalog.setAttribute('aria-busy', 'false');
      this.renderChoice(activeChoice);
    }

    renderChoice(choice) {
      const body = this.panel.querySelector('.idream-service-price-list__body');
      const details = body?.querySelector('[data-price-list-details]');
      if (!body || !details) return;

      const choiceButtons = [...body.querySelectorAll('[data-choice-id]')];
      choiceButtons.forEach((button) => {
        button.setAttribute('aria-expanded', String(button.dataset.choiceId === choice.id));
      });

      const rows = this.config.layout === 'alternative' ? choice.models : choice.services;
      const labelHeading =
        this.config.layout === 'alternative' ? this.config.labels.modelHeading : this.config.labels.serviceHeading;
      const tableHead = document.createElement('div');
      tableHead.className = 'idream-service-price-list__table-head';
      tableHead.setAttribute('aria-hidden', 'true');
      tableHead.append(
        createTextElement('span', labelHeading),
        createTextElement('span', this.config.labels.priceHeading),
      );

      const fragment = document.createDocumentFragment();
      fragment.append(tableHead);
      rows.forEach((row) => fragment.append(this.createPriceRow(row)));
      details.replaceChildren(fragment);

      const activeButton = choiceButtons.find((button) => button.dataset.choiceId === choice.id);
      if (mobileQuery.matches && activeButton) activeButton.after(details);
      else body.append(details);
    }

    createPriceRow(item) {
      const row = document.createElement('div');
      row.className = 'idream-service-price-list__row';
      row.append(
        createTextElement('span', item.title, 'idream-service-price-list__row-label'),
        createTextElement('span', formatPrice(item), 'idream-service-price-list__price'),
      );
      return row;
    }

    renderLoading() {
      this.catalog.setAttribute('aria-busy', 'true');
      const loading = document.createElement('div');
      loading.className = 'idream-service-price-list__loading';
      loading.setAttribute('role', 'status');
      const spinner = document.createElement('span');
      spinner.className = 'idream-service-price-list__spinner';
      spinner.setAttribute('aria-hidden', 'true');
      loading.append(spinner, document.createTextNode(this.config.labels.loading));
      this.panel.replaceChildren(loading);
    }

    renderEmpty() {
      this.catalog.setAttribute('aria-busy', 'false');
      this.panel.replaceChildren(createTextElement('p', this.config.labels.empty, 'idream-service-price-list__empty'));
    }

    renderError(deviceId = '') {
      this.catalog?.setAttribute('aria-busy', 'false');
      if (!this.panel || !this.config) return;

      const wrapper = document.createElement('div');
      wrapper.className = 'idream-service-price-list__error';
      wrapper.setAttribute('role', 'alert');
      wrapper.append(createTextElement('span', this.config.labels.error));

      const retry = document.createElement('button');
      retry.className = 'idream-service-price-list__retry';
      retry.type = 'button';
      retry.dataset.retryDeviceId = deviceId;
      retry.textContent = this.config.labels.retry;
      wrapper.append(retry);
      this.panel.replaceChildren(wrapper);
    }

    onClick(event) {
      const target = event.target instanceof Element ? event.target : null;
      const tab = target?.closest('[role="tab"][data-device-id]');
      if (tab && this.tabs.contains(tab)) {
        void this.selectDevice(tab.dataset.deviceId);
        return;
      }

      const choiceButton = target?.closest('[data-choice-id]');
      if (choiceButton && this.root.contains(choiceButton)) {
        const body = choiceButton.closest('.idream-service-price-list__body');
        const device = this.deviceDetails.get(body?.dataset.deviceId);
        const models = flattenModels(device);
        const choices = this.config.layout === 'alternative' ? groupServices(models) : models;
        const choice = choices.find((item) => item.id === choiceButton.dataset.choiceId);
        if (!choice) return;
        this.selectedChoiceByDevice.set(device.id, choice.id);
        this.renderChoice(choice);
        return;
      }

      const retry = target?.closest('[data-retry-device-id]');
      if (!retry || !this.root.contains(retry)) return;
      if (retry.dataset.retryDeviceId) {
        void this.selectDevice(retry.dataset.retryDeviceId, { refresh: true });
      } else {
        void this.reloadSummary();
      }
    }

    onKeyDown(event) {
      const target = event.target instanceof Element ? event.target : null;
      const tab = target?.closest('[role="tab"][data-device-id]');
      if (!tab || !this.tabs.contains(tab)) return;

      const keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
      if (!keys.includes(event.key)) return;
      event.preventDefault();

      const tabs = [...this.tabs.querySelectorAll('[data-device-id]')];
      const currentIndex = tabs.indexOf(tab);
      let nextIndex = currentIndex;
      if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % tabs.length;
      if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = tabs.length - 1;
      void this.selectDevice(tabs[nextIndex].dataset.deviceId, { focus: true });
    }

    onPrefetchIntent(event) {
      const target = event.target instanceof Element ? event.target : null;
      const tab = target?.closest('[role="tab"][data-device-id]');
      if (tab && this.tabs.contains(tab)) this.prefetchDevice(tab.dataset.deviceId);
    }

    onMediaChange() {
      const body = this.panel.querySelector('.idream-service-price-list__body');
      const activeButton = body?.querySelector('[data-choice-id][aria-expanded="true"]');
      if (!body || !activeButton) return;

      const device = this.deviceDetails.get(body.dataset.deviceId);
      const models = flattenModels(device);
      const choices = this.config.layout === 'alternative' ? groupServices(models) : models;
      const choice = choices.find((item) => item.id === activeButton.dataset.choiceId);
      if (choice) this.renderChoice(choice);
    }

    async reloadSummary() {
      this.renderLoading();
      try {
        const response = await this.requestPrices('', { refresh: true });
        this.devices = orderDevices(asArray(response.devices), this.config.presentation.order);
        this.deviceDetails.clear();
        this.renderHero();
        this.renderTabs();
        if (this.devices.length === 0) {
          this.renderEmpty();
          return;
        }
        await this.selectDevice(this.devices[0].id, { refresh: true });
      } catch {
        this.renderError();
      }
    }
  }

  function flattenModels(device) {
    const models = [];
    const seenIds = new Set();
    asArray(device?.subtypes).forEach((subtype) => {
      asArray(subtype.models).forEach((model) => {
        if (!model?.id || seenIds.has(model.id)) return;
        seenIds.add(model.id);
        models.push({
          id: model.id,
          subtypeId: subtype.id,
          title: model.title || model.id,
          services: asArray(model.services),
        });
      });
    });
    return models;
  }

  function groupServices(models) {
    const groups = new Map();
    models.forEach((model) => {
      model.services.forEach((service) => {
        const id = service.taskId || service.id || service.title;
        if (!groups.has(id)) groups.set(id, { id, title: service.title || id, models: [] });
        groups.get(id).models.push({
          title: model.title,
          priceLabel: service.priceLabel,
          priceAmount: service.priceAmount,
          priceCurrency: service.priceCurrency,
        });
      });
    });
    return [...groups.values()];
  }

  function formatPrice(item) {
    if (item.priceLabel) return item.priceLabel;
    if (!item.priceAmount) return '';
    const amount = Number(item.priceAmount);
    if (!Number.isFinite(amount)) return String(item.priceAmount);
    try {
      return new Intl.NumberFormat('pl-PL', {
        style: 'currency',
        currency: item.priceCurrency || 'PLN',
        maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
      }).format(amount);
    } catch {
      return `${item.priceAmount} ${item.priceCurrency || 'PLN'}`;
    }
  }

  function normalizePresentation(value) {
    const presentation = value && typeof value === 'object' ? value : {};
    const configuredDevices =
      presentation.devices && typeof presentation.devices === 'object' ? presentation.devices : {};
    const devices = {};

    Object.entries(configuredDevices).forEach(([id, device]) => {
      if (!id || !device || typeof device !== 'object') return;
      devices[id] = {
        label: typeof device.label === 'string' ? device.label.trim() : '',
        icon: typeof device.icon === 'string' ? device.icon.trim() : '',
        width: normalizeIconWidth(device.width),
      };
    });

    return {
      order: asArray(presentation.order).filter((id) => typeof id === 'string' && id),
      devices,
      hero: typeof presentation.hero === 'string' ? presentation.hero.trim() : '',
    };
  }

  function normalizeIconWidth(value) {
    const width = Number(value);
    return Number.isFinite(width) && width > 0 ? Math.min(width, 120) : 0;
  }

  function orderDevices(devices, preferredOrder) {
    const positions = new Map(preferredOrder.map((id, index) => [id, index]));
    return devices
      .map((device, index) => ({ device, index }))
      .sort((left, right) => {
        const leftPosition = positions.get(left.device?.id);
        const rightPosition = positions.get(right.device?.id);
        if (leftPosition === undefined && rightPosition === undefined) return left.index - right.index;
        if (leftPosition === undefined) return 1;
        if (rightPosition === undefined) return -1;
        return leftPosition - rightPosition;
      })
      .map(({ device }) => device);
  }

  function createTextElement(tagName, text, className = '') {
    const element = document.createElement(tagName);
    if (className) element.className = className;
    element.textContent = String(text || '');
    return element;
  }

  function asArray(value) {
    return Array.isArray(value) ? value : [];
  }

  function priceListRoots(scope) {
    const candidates = [];
    if (scope instanceof Element && scope.matches('[data-idream-service-price-list]')) candidates.push(scope);
    if (!scope || typeof scope.querySelectorAll !== 'function') return candidates;
    return candidates.concat([...scope.querySelectorAll('[data-idream-service-price-list]')]);
  }

  function initWithin(scope = document) {
    priceListRoots(scope).forEach((root) => {
      if (roots.has(root) || root.dataset.initialized === 'true') return;
      const controller = new ServicePriceList(root);
      roots.set(root, controller);
      void controller.init();
    });
  }

  window.IdreamServicePriceList = Object.freeze({ initWithin });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initWithin(), { once: true });
  } else {
    initWithin();
  }
  document.addEventListener('shopify:section:load', (event) => initWithin(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    priceListRoots(event.target).forEach((root) => roots.get(root)?.destroy());
  });
})();
